import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { shipmentAPI } from '../../services/api';
import EstimateBreakdown from './EstimateBreakdown';
import EstimateComparison from './EstimateComparison';

const STATUS_BADGE_CLASS = {
  PENDING_QUOTE: 'badge-pending-quote',
  QUOTED: 'badge-quoted',
  QUOTE_CONFIRMED: 'badge-confirmed',
  BOOKED: 'badge-booked',
  IN_TRANSIT: 'badge-in-transit',
  DELIVERED: 'badge-delivered'
};

const ShipmentDetail = () => {
  const { id } = useParams();
  const [shipment, setShipment] = useState(null);
  const [quotes, setQuotes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [shipmentResult, quotesResult] = await Promise.allSettled([
          shipmentAPI.getById(id),
          shipmentAPI.getQuotes(id)
        ]);

        if (shipmentResult.status === 'fulfilled') {
          setShipment(shipmentResult.value.data);
        } else {
          setError('Shipment not found');
          return;
        }

        if (quotesResult.status === 'fulfilled') {
          setQuotes(quotesResult.value.data);
        }
      } catch (err) {
        setError('Failed to load shipment details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not specified';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatCost = (cost) => {
    if (!cost) return 'Not set';
    return `$${Number(cost).toLocaleString()} USD`;
  };

  if (loading) return <div className="loading">Loading shipment...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!shipment) return <div className="error">Shipment not found</div>;

  return (
    <div className="shipment-detail">
      <div className="page-header">
        <h1>
          Shipment {shipment.orderId || shipment.id.slice(0, 8)}
          <span className={`badge ${STATUS_BADGE_CLASS[shipment.status] || ''}`} style={{ marginLeft: '1rem', fontSize: '0.875rem' }}>
            {shipment.status.replace(/_/g, ' ')}
          </span>
        </h1>
        <div className="header-actions">
          <Link to="/shipments" className="btn btn-secondary">Back to List</Link>
          {shipment.status === 'QUOTED' && (
            <Link to={`/shipments/${shipment.id}/quotes/compare`} className="btn btn-primary">
              Compare Quotes
            </Link>
          )}
          {['BOOKED', 'IN_TRANSIT', 'DELIVERED', 'QUOTE_CONFIRMED'].includes(shipment.status) && (
            <Link to={`/shipments/${shipment.id}/tracking`} className="btn btn-primary">
              Track Shipment
            </Link>
          )}
        </div>
      </div>

      <div className="detail-grid">
        {/* Shipment Info */}
        <div className="detail-section">
          <h2>Shipment Details</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Shipment ID</label>
              <span>{shipment.id}</span>
            </div>
            <div className="info-item">
              <label>Order ID</label>
              <span>{shipment.orderId || 'Not assigned'}</span>
            </div>
            <div className="info-item">
              <label>Origin Port</label>
              <span>{shipment.originPort}</span>
            </div>
            <div className="info-item">
              <label>Destination Port</label>
              <span>{shipment.destinationPort}</span>
            </div>
            <div className="info-item">
              <label>Delivery Address</label>
              <span>{shipment.destinationAddress || 'Port destination'}</span>
            </div>
            <div className="info-item">
              <label>Container</label>
              <span>{shipment.containerCount}x {shipment.containerType}</span>
            </div>
            <div className="info-item">
              <label>Commodity</label>
              <span>{shipment.commodity || 'General cargo'}</span>
            </div>
            {shipment.totalWeight && (
              <div className="info-item">
                <label>Total Weight</label>
                <span>{shipment.totalWeight} kg</span>
              </div>
            )}
          </div>
        </div>

        {/* Dates & Cost */}
        <div className="detail-section">
          <h2>Dates & Cost</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Cargo Ready Date</label>
              <span>{formatDate(shipment.cargoReadyDate)}</span>
            </div>
            <div className="info-item">
              <label>Requested Delivery</label>
              <span>{formatDate(shipment.requestedDeliveryDate)}</span>
            </div>
            <div className="info-item">
              <label>Estimated Freight Cost</label>
              <span>{formatCost(shipment.estimatedFreightCost)}</span>
            </div>
            <div className="info-item">
              <label>Actual Freight Cost</label>
              <span>{formatCost(shipment.actualFreightCost)}</span>
            </div>
            <div className="info-item">
              <label>Created</label>
              <span>{formatDate(shipment.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Stakeholder Emails */}
        {(shipment.sellerEmail || shipment.buyerEmail) && (
          <div className="detail-section">
            <h2>Stakeholders</h2>
            <div className="info-grid">
              {shipment.sellerEmail && (
                <div className="info-item">
                  <label>Seller Email</label>
                  <span>{shipment.sellerEmail}</span>
                </div>
              )}
              {shipment.buyerEmail && (
                <div className="info-item">
                  <label>Buyer Email</label>
                  <span>{shipment.buyerEmail}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quotes Section */}
        {quotes && quotes.quotes && quotes.quotes.length > 0 && (
          <div className="detail-section full-width">
            <h2>Quotes ({quotes.quotes.length})</h2>
            <table className="simple-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Total Cost</th>
                  <th>Transit Days</th>
                  <th>Status</th>
                  <th>Selected</th>
                </tr>
              </thead>
              <tbody>
                {quotes.quotes.map(quote => (
                  <tr key={quote.id} className={quote.selected ? 'recommended' : ''}>
                    <td>{quote.provider?.name || quote.providerId?.slice(0, 8)}</td>
                    <td>{quote.totalCost ? `$${Number(quote.totalCost).toLocaleString()}` : 'Pending'}</td>
                    <td>{quote.transitDays || '-'}</td>
                    <td>
                      <span className={`badge ${quote.status === 'COMPLETE' ? 'badge-active' : 'badge-inactive'}`}>
                        {quote.status}
                      </span>
                    </td>
                    <td>{quote.selected ? 'Yes' : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {shipment.status === 'QUOTED' && (
              <div className="card-footer">
                <Link to={`/shipments/${shipment.id}/quotes/compare`} className="btn btn-primary">
                  Compare & Select Quote
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Freight Estimate Breakdown */}
        <EstimateBreakdown shipmentId={id} />

        {/* Estimate vs Actual Comparison (only renders when actuals exist) */}
        <EstimateComparison shipmentId={id} />

        {/* Booking Details */}
        {(shipment.status === 'QUOTE_CONFIRMED' || shipment.status === 'BOOKED' || shipment.status === 'IN_TRANSIT' || shipment.status === 'DELIVERED') && (
          <div className="detail-section">
            <h2>Booking Details</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Confirmed Provider</label>
                <span>{shipment.confirmedProviderId ? shipment.confirmedProviderId.slice(0, 8) + '...' : '-'}</span>
              </div>
              <div className="info-item">
                <label>Selected Quote</label>
                <span>{shipment.selectedQuoteId ? shipment.selectedQuoteId.slice(0, 8) + '...' : '-'}</span>
              </div>
              <div className="info-item">
                <label>Booked Cost</label>
                <span>{formatCost(shipment.actualFreightCost)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipmentDetail;
